import type { ComponentType } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useBrand } from '@/features/branding/brand-provider';
import GdprEn from '@/markdown/gdpr_policy_en.mdx';
import GdprSv from '@/markdown/gdpr_policy_sv.mdx';
import PrivacyEn from '@/markdown/privacy_policy_en.mdx';
import PrivacySv from '@/markdown/privacy_policy_sv.mdx';
import TermsEn from '@/markdown/terms_of_service_en.mdx';
import TermsSv from '@/markdown/terms_of_service_sv.mdx';

export type LegalDoc = 'gdpr' | 'privacy-policy' | 'terms-of-service';

// Legal docs exist in English and Swedish only (matching the Next app's
// gdpr/{en,sv} pages); any other locale falls back to English.
const DOCS: Record<LegalDoc, { en: ComponentType; sv: ComponentType }> = {
  gdpr: { en: GdprEn, sv: GdprSv },
  'privacy-policy': { en: PrivacyEn, sv: PrivacySv },
  'terms-of-service': { en: TermsEn, sv: TermsSv },
};

export function LegalPage({ doc }: { doc: LegalDoc }) {
  const { slug, brand } = useBrand();
  const { locale } = useParams<{ locale: string }>();
  const lang = locale === 'sv' ? 'sv' : 'en';
  const Content = DOCS[doc][lang];
  const other = lang === 'sv' ? 'en' : 'sv';

  return (
    <div className="bg-muted/40 min-h-dvh w-full px-4 py-16">
      <div className="mx-auto w-full max-w-screen-lg">
        <img
          src={brand.logo}
          width={294}
          height={91}
          alt={`${brand.name} logo`}
        />

        <div className="mt-16 flex flex-row justify-between px-4">
          <Link
            to={`/${slug}/${doc}/${other}`}
            className="text-muted-foreground mt-4 text-lg"
          >
            {other === 'sv' ? 'På svenska' : 'In English'}
          </Link>
          <div className="flex flex-row justify-between gap-6">
            <Link
              to={`/${slug}/privacy-policy/${lang}`}
              className="text-muted-foreground mt-4 text-lg"
            >
              Privacy Policy
            </Link>
            <Link
              to={`/${slug}/terms-of-service/${lang}`}
              className="text-muted-foreground mt-4 text-lg"
            >
              Terms of Service
            </Link>
          </div>
        </div>

        <section className="prose-h6:text-md prose bg-background prose-headings:mt-8 prose-headings:font-semibold prose-headings:text-black prose-h1:text-4xl prose-h2:text-3xl prose-h3:text-2xl prose-h4:text-xl prose-h5:text-lg dark:prose-headings:text-white mt-4 w-full max-w-none rounded-md p-16 shadow-md">
          <Content />
        </section>
      </div>
    </div>
  );
}
